/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

/**
 * Carries a list page's filters across a round trip to another screen and back.
 *
 * The list pages keep their filters in component state, not in the URL, so leaving one and returning
 * re-mounts it with the defaults — the operator who had just cleared every status, widened the dates
 * and found the one booking they cared about lands back on the default view and has to do it again.
 *
 * Deliberately sessionStorage and deliberately single-use: the snapshot belongs to one journey out
 * and back, not to the page forever. A reload, a new tab or a second visit all start fresh, which is
 * what people expect from a filter bar; only the explicit "Back" from the screen we sent them to
 * restores it.
 *
 * Every access is guarded: storage throws in private windows and when site data is blocked, and a
 * filter convenience must never be the thing that breaks a page.
 */

const PREFIX = 'returnFilters:';

/** Stores the snapshot for `key` (use the page's path, e.g. "/bookings"). */
export function saveReturnFilters(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // No storage — the return trip simply lands on the default filters.
  }
}

/**
 * Reads the snapshot for `key` and removes it, so a later visit to the same page is not silently
 * restored to filters the operator has long forgotten setting.
 */
export function takeReturnFilters<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    sessionStorage.removeItem(PREFIX + key);
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
